import React, { useRef, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import logo from "../../Assets/Logos/QVLogo4.png";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";

const BottomNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMobileCat, setActiveMobileCat] = useState(null);
  const [activeMobileSub, setActiveMobileSub] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();

  const handleNavigate = (path) => {
  navigate(path);

  // 🔥 CLOSE EVERYTHING
  setIsMenuOpen(false);
  setActiveMobileCat(null);
  setActiveMobileSub(null);
  setActiveDropdown(null);   
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const timeoutRef = useRef();

const handleMouseEnter = (label) => {
  if (timeoutRef.current) clearTimeout(timeoutRef.current);
  setActiveDropdown(label);
};

const handleMouseLeave = () => {
  timeoutRef.current = setTimeout(() => {
    setActiveDropdown(null);
  }, 150);
};

const serviceOptions = [
  {
    label: "Software Development",
    slug: "software-development", 
    options: [
      { title: "Custom Enterprise Software", slug: "custom-enterprise-software" },
      { title: "Web & Mobile Applications", slug: "web-mobile-applications" },
      { title: "SaaS Platforms", slug: "saas-platforms" },
      { title: "Data & Analytics Platforms", slug: "data-analytics-platforms" },
      { title: "AI & Predictive Systems", slug: "ai-predictive-systems" },
    ],
  },
  {
    label: "Engineering Digital Solutions",
    slug: "engineering-digital-solutions", 
    options: [
      { title: "Life Sciences & Biotech Intelligence Systems (LIMS)", slug: "lims" },
      { title: "Electronic Lab Notebooks (ELN)", slug: "electronic-lab-notebooks" },
      { title: "Clinical Data Management Systems (CDMS)", slug: "clinical-data-management-systems" },
      { title: "Research Data Analytics & AI", slug: "research-data-analytics-ai" },
      { title: "Biotech Manufacturing Systems (GMP Systems)", slug: "biotech-manufacturing-systems-gmp" },
    ],
  },
  {
    label: "Construction Tech (Project Areas)",
    slug: "construction-tech", 
    options: [
      { title: "BIM & Digital Twin Systems", slug: "bim-digital-twin-systems" },
      { title: "Project Planning & Scheduling Systems", slug: "project-planning-scheduling-systems" },
      { title: "Site Monitoring & Field Management", slug: "site-monitoring-field-management" },
      { title: "Equipment & Asset Management", slug: "equipment-asset-management" },
      { title: "Construction Energy & Resource Monitoring", slug: "construction-energy-resource-monitoring" },
      { title: "Procurement & Supply Chain Systems", slug: "procurement-supply-chain-systems" },
    ],
  },
   {
    label: "Manufacturing",
    slug: "manufacturing", 
    options: [
      { title: "Smart Factory & Industry 4.0 Platform Development", slug: "smart-factory-industry-4-0-platforms" },
      { title: "Manufacturing Execution Systems (MES)", slug: "manufacturing-execution-systems-mes" },
      { title: "Production Planning & Scheduling Systems", slug: "production-planning-scheduling-systems" },
      { title: "IoT-Based Machine Monitoring & Data Capture", slug: "iot-machine-monitoring-data-capture" },
      { title: "Predictive Maintenance Platforms", slug: "predictive-maintenance-platforms" },
    ],
  },
  {
    label: "Cloud & Infrastructure (VDI Focus)",
    slug: "cloud-infrastructure", 
    options: [
      { title: "Virtual Desktop Infrastructure (VDI)", slug: "virtual-desktop-infrastructure-vdi" },
      { title: "Cloud Migration", slug: "cloud-migration" },
      { title: "DevOps Automation", slug: "devops-automation" },
    ],
  },
  {
    label: "Data & AI Solutions",
    slug: "data-ai-solutions", 
    options: [
      { title: "Predictive Analytics", slug: "predictive-analytics" },
      { title: "Supply Chain Optimization (AI-driven)", slug: "supply-chain-optimization" },
      { title: "Process Automation", slug: "process-automation" },
    ],
  },
];

  const industiesOptions = [
    { title: "Life sciences & Biotech", slug: "life-sciences-biotech" },
    { title: "Construction & Civil Engineering", slug: "construction-civil-engineering" },
    { title: "Manufacturing & Industry 4.0 ", slug: "manufacturing-industry" },
    { title: "Supply Chain & Logistics", slug: "supply-chain-logistics" },
  ];

const navItems = [
  { label: "About", path: "/about" },

  {
    label: "Services",
    path: "/services",
    categories: serviceOptions,
  },

  {
    label: "Industries",
    path: "/industries",
    categories: [
      {
        hideLabel: true,
        options: industiesOptions,
      },
    ],
  },

  { label: "Contact", path: "/contact" },
];

  return (
    <nav className="shadow-lg backdrop-blur-md bg-gradient-to-br from-[#F8F8F2] to-gray-50 border-b border-gray-100 sticky top-0 z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo */}
          <div className="relative group cursor-pointer" onClick={() => handleNavigate("/")}>
            <div 
            onClick={()=>{
              navigate("/");
              window.scrollTo({
                top : 0,
                behavior : "smooth"
              })
            }}
            className="relative overflow-hidden rounded-2xl px-2 bg-gradient-to-br from-[#F8F8F2] to-gray-50">
              <img
                src={logo}
                alt="Logo"
                className="h-10 md:h-12 lg:h-16 w-auto transform transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          </div>

          {/* ── Desktop Nav ── */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >

                <button
                 onClick={() => handleNavigate(item.path || `/${item.label.toLowerCase()}`)}
                  className="flex items-center gap-1 px-5 py-3 text-gray-700 font-semibold cursor-pointer hover:text-[#041427] transition-colors duration-200"
                >
                  {item.label}
                  {item.categories && (
                    <IoIosArrowDown
                      size={15}
                      className="transition-transform duration-300 "
                    />
                  )}
                </button>

                {/* ── Level 1 dropdown: only category labels ── */}
                {item.categories && (
                  <div
                      className={`absolute left-0 top-full pt-2 z-50 transition-all duration-200 ease-out ${
                        activeDropdown === item.label
                          ? "opacity-100 visible translate-y-0"
                          : "opacity-0 invisible -translate-y-1"
                      }`}
                    >
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-visible min-w-[320px]">
                      {/* gradient bar */}
                      <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-t-2xl" />

                      {item.categories.map((cat) => (
                        // Category row — hover reveals L2
                        
                        <div key={cat.label} className="relative ">
                         
                        {cat.hideLabel ? (
                        <div className="p-2 ">
                                {cat.options.map((opt) => (
                                  <div
                                    key={opt.slug}
                                    onClick={() =>{
                                      handleNavigate(`/industries/${opt.slug}`);
                                      window.scrollTo({
                                        top:0,
                                        behavior: "smooth"
                                      })
                                    }}
                                    className="px-4 py-2 rounded-md flex items-center justify-between cursor-pointer text-base font-semibold text-gray-700 hover:bg-gray-100"
                                  >
                                    {opt.title}

                                    <span><ArrowRight size={15}/></span>
                                  </div>
                                ))}
                              </div>
                      ) : (
                        // 🔹 SERVICES (2-level)
                        <div className="group/cat min-w-[280px]">
                          <div onClick={() => {
                            handleNavigate(`/services/${cat.slug}`);
                          
                        }} 
                          
                          className="flex items-center cursor-pointer justify-between px-4 py-3 text-gray-700 font-semibold text-base">
                            <span>{cat.label}</span>
                            <IoIosArrowForward size={13} />
                          </div>

                          <div className="absolute w-80 left-full top-[-4px] pl-2 opacity-0 invisible group-hover/cat:visible group-hover/cat:opacity-100 transition-all">
                            <div className="bg-white rounded-2xl shadow-xl min-w-[210px]">
                              {cat.options.map((opt) => (
                                <div
                                  key={opt.slug}
                                  onClick={() =>{
                                      handleNavigate(`/services/${cat.slug}/${opt.slug}`);
                                    }}
                                  className="px-4 py-2.5 text-base hover:bg-gray-100 cursor-pointer"
                                >
                                  {opt.title}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div> 
                      )}

                          {/* ── Level 2: sub-options fly out to the right ── */}
                          <div className="
                            absolute left-full top-[-4px] pl-2 z-50
                            opacity-0 invisible translate-x-[-6px]
                            group-hover/cat:opacity-100 group-hover/cat:visible group-hover/cat:translate-x-0
                            transition-all duration-200 ease-out
                          ">
                            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden min-w-[210px]">
                              <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
                              {cat.options.map((opt) => (
                                <div
                                  key={opt.slug}
                                  onClick={() => handleNavigate(`/services/${opt.slug}`)}
                                  className="group/item flex items-center justify-between px-4 py-2.5 cursor-pointer text-gray-700 text-sm font-medium hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-[#041427] transition-all duration-200"
                                >
                                  <span>{opt.title}</span>
                                  <span className="opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all duration-200 text-blue-500 text-xs">
                                    →
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-lg blur opacity-70 group-hover:opacity-100 transition duration-300" />
              <button
                onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="relative px-8 py-3 bg-[#041427] text-white rounded-lg font-bold tracking-wide transition-all duration-300 group-hover:scale-105 shadow-xl"
              >
                Plan With Us
              </button>
            </div>
          </div>

          {/* Hamburger */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-3 rounded-xl bg-gradient-to-br from-gray-50 to-white shadow-md hover:shadow-lg transition-all duration-300"
            >
              {isMenuOpen
                ? <X size={24} className="text-gray-700" />
                : <Menu size={24} className="text-gray-700" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile menu ── */}
      <div className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${isMenuOpen ? "max-h-[100vh] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="bg-gradient-to-b from-white to-gray-50 border-t border-gray-100 shadow-inner">
          <div className="px-4 pt-4 pb-6 space-y-2">
            {navItems.map((item, index) => (
              <div key={index} className="w-full">

                {/* Top level */}
                <button
                  onClick={() => {
                    if (item.categories) {
                      setActiveMobileCat(activeMobileCat === index ? null : index);
                      setActiveMobileSub(null);
                    } else {
                      handleNavigate(item.path);
                    }
                  }}
                  className="w-full flex items-center justify-between px-4 py-4 rounded-xl bg-white text-gray-700 font-semibold transition-all duration-300 active:scale-[0.97] shadow-sm"
                >
                  <span>{item.label}</span>
                  {item.categories && (
                    <IoIosArrowDown size={18} className={`transition-transform duration-300 ${activeMobileCat === index ? "rotate-180" : ""}`} />
                  )}
                </button>

                {/* Category level */}
                {item.categories && (
                  <div className={`overflow-hidden transition-all duration-300 ${activeMobileCat === index ? "max-h-[600px] opacity-100 mt-2" : "max-h-0 opacity-0"}`}>
                    <div className="ml-4 border-l-2 border-gray-100 pl-3 space-y-1">
                     {item.categories.map((cat) => (
                        <div key={cat.label || "no-label"}>

                          {!cat.hideLabel && (
                            <button
                              onClick={() =>
                                setActiveMobileSub(
                                  activeMobileSub === cat.label ? null : cat.label
                                )
                              }
                              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 text-gray-700 font-semibold text-sm"
                            >
                              <span>{cat.label}</span>
                              <IoIosArrowDown
                                size={14}
                                className={`transition-transform duration-300 ${
                                  activeMobileSub === cat.label ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                          )}

                          {/* ── INDUSTRIES (no label, no accordion) ── */}
                          {cat.hideLabel && (
                            <div className="ml-2 space-y-1">
                              {cat.options.map((opt) => (
                                <button
                                  key={opt.slug}
                                  onClick={() =>
                                    handleNavigate(`/industries`)
                                  }
                                  className="block w-full text-left px-3 py-2 rounded-md text-gray-600 text-sm hover:bg-gray-100"
                                >
                                  {opt.title}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* ── SERVICES (accordion) ── */}
                          {!cat.hideLabel && (
                            <div
                              className={`overflow-hidden  transition-all duration-300 ${
                                activeMobileSub === cat.label
                                  ? "max-h-96 opacity-100 mt-1"
                                  : "max-h-0 opacity-0"
                              }`}
                            >
                              <div className="ml-3 right-4 border-l border-gray-100 pl-3 py-1 space-y-0.5">
                                {cat.options.map((opt) => (
                                  <button
                                    key={opt.slug}
                                    onClick={() =>
                                      handleNavigate(`/services/${opt.slug}`)
                                    }
                                    className="block w-full text-left px-2 py-2 rounded-md text-gray-600 text-sm hover:bg-blue-50"
                                  >
                                    {opt.title}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            ))}

            <div className="pt-6">
              <button
                onClick={() => { handleNavigate("/"); setIsMenuOpen(false); }}
                className="w-full bg-[#041427] text-white py-4 rounded-xl font-bold text-lg shadow-xl active:scale-95 transition"
              >
                Plan With Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default BottomNavbar;